import ErrorText from '@data/components/basic/ErrorText/ErrorText';
import LoadingSpinner from '@data/components/basic/LoadingSpinner/LoadingSpinner';
import { useGetMeApi } from 'eds-common/api/v2';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

export interface ViewSettings {
  title: string;
  overviewType: 'org' | 'class';
  viewBy: 'schools' | 'grades' | 'months';
}

type MutateFn = <K extends keyof ViewSettings>(
  k: K,
  filter: ViewSettings[K]
) => void;

export interface ViewSettingsContextProps {
  settings: ViewSettings;
  mutate: MutateFn;
  enablements: Record<keyof ViewSettings, boolean>;
}

const defaultContext: ViewSettingsContextProps = {
  settings: {
    title: '',
    overviewType: 'org',
    viewBy: 'schools',
  },
  mutate: () => {},
  enablements: {
    title: true,
    overviewType: true,
    viewBy: true,
  },
};

export const ViewSettingsContext =
  createContext<ViewSettingsContextProps>(defaultContext);

export function ViewSettingsProvider({ children }: PropsWithChildren) {
  const { '*': splat, teacherId } = useParams();
  const {
    data: user,
    isLoading: userLoading,
    isError: userError,
  } = useGetMeApi();

  const currentRole = user?.currentRole ?? 'teacher';

  const [settings, setSettings] = useState<ViewSettings>({
    title: '',
    overviewType:
      currentRole === 'teacher' || splat?.startsWith('class') ? 'class' : 'org',
    viewBy: 'schools',
  });

  const enablements: Record<keyof ViewSettings, boolean> = useMemo(
    () => ({
      title: true,
      overviewType: currentRole === 'administrator' && !teacherId,
      viewBy: currentRole === 'administrator' && !teacherId,
    }),
    [currentRole, teacherId]
  );

  const mutate = useCallback<MutateFn>((k, val) => {
    setSettings((ss) => ({ ...ss, [k]: val }));
  }, []);

  const value: ViewSettingsContextProps = useMemo(
    () => ({
      settings,
      mutate,
      enablements,
    }),
    [enablements, mutate, settings]
  );

  if (userLoading)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );

  if (userError) return <ErrorText label="Error fetching user information" />;

  return (
    <ViewSettingsContext.Provider value={value}>
      {children}
    </ViewSettingsContext.Provider>
  );
}

export function useViewSetting<K extends keyof ViewSettings>(k: K) {
  const {
    settings,
    mutate: mutateBase,
    enablements,
  } = useContext(ViewSettingsContext);

  const mutate = useCallback(
    (val: ViewSettings[K]) => mutateBase(k, val),
    [k, mutateBase]
  );

  return [settings[k], mutate, enablements[k]] as const;
}
